import {
  startAuthentication,
  startRegistration,
  browserSupportsWebAuthn,
} from '@simplewebauthn/browser';

export interface AuthUser {
  userId: string;
  userDetails: string;
  identityProvider: string;
  userRoles: string[];
  username?: string;
  role?: 'admin' | 'user' | string;
  extraStateSlots?: number;
  extraPsxCards?: number;
  ownedGameIds?: string[];
  fullAccess?: boolean;
}

export interface AuthStatus {
  userCount: number;
  bootstrapOpen: boolean;
  inviteRequired: boolean;
  emailConfigured: boolean;
}

async function postJson<T>(url: string, body: unknown): Promise<T> {
  const res = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = (await res.json().catch(() => ({}))) as T & { error?: string };
  if (!res.ok) {
    throw new Error(data.error || `リクエストに失敗しました (${res.status})`);
  }
  return data;
}

export function passkeySupported(): boolean {
  return browserSupportsWebAuthn();
}

export async function fetchAuthStatus(): Promise<AuthStatus | null> {
  try {
    const res = await fetch('/api/auth/status', { credentials: 'include' });
    if (!res.ok) return null;
    return (await res.json()) as AuthStatus;
  } catch {
    return null;
  }
}

export async function fetchAuthUser(): Promise<AuthUser | null> {
  try {
    const res = await fetch('/api/auth/me', { credentials: 'include' });
    if (!res.ok) return null;
    const body = (await res.json()) as { clientPrincipal: AuthUser | null };
    return body.clientPrincipal;
  } catch {
    return null;
  }
}

export async function peekInvite(token: string): Promise<{ email: string; expiresAt: string } | null> {
  try {
    const res = await fetch(`/api/auth/invite/${encodeURIComponent(token)}`, { credentials: 'include' });
    if (!res.ok) return null;
    return (await res.json()) as { email: string; expiresAt: string };
  } catch {
    return null;
  }
}

export async function loginWithPassword(username: string, password: string): Promise<AuthUser> {
  const verified = await postJson<{ user: AuthUser }>('/api/auth/password/login', { username, password });
  return verified.user;
}

export async function registerWithPassword(opts: {
  username: string;
  password: string;
  displayName?: string;
  inviteToken?: string;
}): Promise<AuthUser> {
  const verified = await postJson<{ user: AuthUser }>('/api/auth/password/register', opts);
  return verified.user;
}

export async function registerPasskey(
  username: string,
  displayName?: string,
  inviteToken?: string,
): Promise<AuthUser> {
  const prep = await postJson<{
    options: Parameters<typeof startRegistration>[0] extends { optionsJSON: infer O } ? O : never;
    userId: string;
  }>('/api/auth/register/options', { username, displayName, inviteToken });

  let attestation: Awaited<ReturnType<typeof startRegistration>>;
  try {
    attestation = await startRegistration({ optionsJSON: prep.options });
  } catch (e) {
    if (e instanceof Error && /cancel|deny|abort/i.test(e.message)) {
      throw new Error('Passkey の作成がキャンセルされました');
    }
    throw e instanceof Error ? e : new Error(String(e));
  }

  const verified = await postJson<{ user: AuthUser }>('/api/auth/register/verify', {
    userId: prep.userId,
    response: attestation,
  });
  return verified.user;
}

export async function loginPasskey(username?: string): Promise<AuthUser> {
  const prep = await postJson<{
    options: Parameters<typeof startAuthentication>[0] extends { optionsJSON: infer O } ? O : never;
    challengeKey: string;
  }>('/api/auth/login/options', { username: username || undefined });

  let assertion: Awaited<ReturnType<typeof startAuthentication>>;
  try {
    assertion = await startAuthentication({ optionsJSON: prep.options });
  } catch (e) {
    if (e instanceof Error && /cancel|deny|abort/i.test(e.message)) {
      throw new Error('Passkey 認証がキャンセルされました');
    }
    throw e instanceof Error ? e : new Error(String(e));
  }

  const verified = await postJson<{ user: AuthUser }>('/api/auth/login/verify', {
    challengeKey: prep.challengeKey,
    response: assertion,
  });
  return verified.user;
}

export async function logoutPasskey(): Promise<void> {
  await postJson('/api/auth/logout', {});
}

export async function fetchStoreCatalog(): Promise<{
  games: Array<{ id: string; title: string; purchasable: boolean }>;
  stripeConfigured: boolean;
}> {
  const res = await fetch('/api/store/catalog', { credentials: 'include' });
  if (!res.ok) return { games: [], stripeConfigured: false };
  return (await res.json()) as {
    games: Array<{ id: string; title: string; purchasable: boolean }>;
    stripeConfigured: boolean;
  };
}

export async function startCheckout(kind: 'extra-state' | 'extra-psx-card' | 'game', gameId?: string): Promise<void> {
  const data = await postJson<{ url: string }>('/api/billing/checkout', { kind, gameId });
  if (!data.url) throw new Error('決済ページを開けませんでした');
  window.location.assign(data.url);
}

export interface AccountSummary {
  username: string;
  displayName: string;
  role: string;
  fullAccess: boolean;
}

export async function listAccounts(): Promise<AccountSummary[]> {
  const res = await fetch('/api/auth/users', { credentials: 'include' });
  const data = (await res.json().catch(() => ({}))) as { users?: AccountSummary[]; error?: string };
  if (!res.ok) throw new Error(data.error || 'ユーザー一覧を取得できませんでした');
  return data.users ?? [];
}

export async function setFullAccess(username: string, enabled: boolean): Promise<void> {
  await postJson('/api/auth/full-access', { username, enabled });
}

export async function createInvite(email: string): Promise<{
  email: string;
  inviteUrl: string;
  emailSent: boolean;
  emailConfigured: boolean;
  emailError?: string;
}> {
  return postJson('/api/auth/invite', { email });
}

export function readInviteTokenFromUrl(): string | null {
  try {
    const sp = new URLSearchParams(window.location.search);
    return sp.get('invite');
  } catch {
    return null;
  }
}

export function clearInviteFromUrl(): void {
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('invite')) return;
    url.searchParams.delete('invite');
    window.history.replaceState({}, '', url.pathname + url.search + url.hash);
  } catch {
    /* ignore */
  }
}
